import type { ActionItem, ITooltipProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { UserAccessRequestType } from 'entities/Users'
import { noManageInboxesPermissionTooltipLabel } from 'entities/UsersPermissions'
import { type RowAccessRequest } from 'entities/Users/model/Table/RowAccessRequest'
import { ReviewPermissionsStore } from 'pages/settings/pages/members/pages/membersView/ui/RequestsMembersTab/ui/ReviewPermissionsModal/store/reviewPermissionsStore'
import { ReviewSalesmsgAccessStore } from 'pages/settings/pages/members/pages/membersView/ui/RequestsMembersTab/ui/ReviewSalesmsgAccessModal/store/reviewSalesmsgAccessStore'
import { ReviewInboxAccessStore } from 'pages/settings/pages/members/pages/membersView/ui/RequestsMembersTab/ui/ReviewInboxAccessModal/store/reviewInboxAccessStore'
import { RejectAccessRequestStore } from 'pages/settings/pages/members/pages/membersView/ui/RequestsMembersTab/ui/RejectAccessRequestModal/store/rejectAccessRequestStore'
import { type SettingsStore } from 'pages/settings/store/settingsStore'
import { getMemberGracePeriodTooltipProps } from 'pages/settings/pages/members/lib/memberTooltipProps'

type IGetAccessRequestActionsParams = {
  entities: IEntitiesStore
  settings?: SettingsStore
  onSuccess?: () => void
  isReviewDisabledByInboxAccess?: boolean
  isGracePeriod?: boolean
}

export const getAccessRequestActions = (
  item: RowAccessRequest,
  {
    entities,
    settings,
    onSuccess,
    isReviewDisabledByInboxAccess,
    isGracePeriod,
  }: IGetAccessRequestActionsParams
): ActionItem[] => {
  if (item.origin.requestType === UserAccessRequestType.PermissionsUpdate) {
    return [
      {
        buttonProps: {
          intent: 'destructive',
          contained: 'secondary',
        },
        text: 'Reject',
        onAction: () => {
          new RejectAccessRequestStore(onSuccess).openModal(item.origin)
        },
      },
      {
        buttonProps: {
          contained: 'primary',
        },
        text: 'Review',
        onAction: async () => {
          new ReviewPermissionsStore(entities, onSuccess).openModal(item.origin)
        },
      },
    ]
  }

  if (item.origin.requestType === UserAccessRequestType.SalesmsgAccess && settings) {
    const isReviewDisabled = isReviewDisabledByInboxAccess || isGracePeriod

    const getTooltipProps = (): ITooltipProps | undefined => {
      if (isGracePeriod) {
        return getMemberGracePeriodTooltipProps()
      }
      if (isReviewDisabledByInboxAccess) {
        return {
          label: noManageInboxesPermissionTooltipLabel,
          placement: 'top',
          breakWord: true,
        }
      }
      return undefined
    }

    return [
      {
        buttonProps: {
          intent: 'destructive',
          contained: 'secondary',
        },
        text: 'Reject',
        onAction: () => {
          new RejectAccessRequestStore(onSuccess).openModal(item.origin)
        },
      },
      {
        buttonProps: {
          contained: 'primary',
          disabled: isReviewDisabled,
          tooltipProps: getTooltipProps(),
        },
        text: 'Review',
        onAction: () => {
          new ReviewSalesmsgAccessStore(entities, settings, onSuccess).openModal(item.origin)
        },
      },
    ]
  }

  if (item.origin.requestType === UserAccessRequestType.InboxAccess && settings) {
    return [
      {
        buttonProps: {
          intent: 'destructive',
          contained: 'secondary',
        },
        text: 'Reject',
        onAction: () => {
          new RejectAccessRequestStore(onSuccess).openModal(item.origin)
        },
      },
      {
        buttonProps: {
          contained: 'primary',
          disabled: isReviewDisabledByInboxAccess,
          tooltipProps: isReviewDisabledByInboxAccess
            ? {
                label: noManageInboxesPermissionTooltipLabel,
                placement: 'top',
                breakWord: true,
              }
            : undefined,
        },
        text: 'Review',
        onAction: () => {
          new ReviewInboxAccessStore(entities, settings, onSuccess).openModal(item.origin)
        },
      },
    ]
  }

  return [
    {
      text: 'Review',
      onAction: () => {},
    },
  ]
}
